import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, SectionHeading, Reveal, Overline, CTAButton } from "../components/Kinetic";
import { SubmissionForm } from "../components/SubmissionForm";
import { ArrowUpRight } from "lucide-react";

const PAYPAL_URL =
  "https://www.paypal.com/cgi-bin/webscr?cmd=_donations&business=cyrilone2002%40hotmail.com&item_name=Green%20Horizon%20Project&currency_code=EUR";

const SoutenirPage = () => {
  const { t } = useLanguage();
  useSEO(t("soutenir.metaTitle"), t("soutenir.metaDesc"));
  const options = t("soutenir.options");

  const fields = [
    { name: "name", label: t("forms.name"), type: "text" },
    { name: "email", label: t("forms.email"), type: "email" },
    { name: "organization", label: t("forms.organization"), type: "text", required: false },
    { name: "supportType", label: t("forms.supportType"), type: "select", options: t("forms.supportTypeOptions") },
    { name: "message", label: t("forms.message"), type: "textarea" },
  ];

  return (
    <div data-testid="soutenir-page">
      <PageHero
        overline={t("soutenir.overline")}
        titleLines={t("soutenir.titleLines")}
        intro={t("soutenir.intro")}
        testid="soutenir"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading overline={t("soutenir.optionsOverline")} title={t("soutenir.optionsTitle")} />
          <div className="mt-16 grid gap-px bg-graphite/10 sm:grid-cols-2 lg:grid-cols-4">
            {options.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.05} className="group bg-bone p-7 transition-colors duration-300 hover:bg-forest">
                <p className="font-mono text-xs text-forest transition-colors duration-300 group-hover:text-white/60">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-tight text-graphite transition-colors duration-300 group-hover:text-white">
                  {o.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-graphite-soft transition-colors duration-300 group-hover:text-white/70">
                  {o.text}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-12 max-w-3xl border-l-2 border-forest pl-6 text-sm leading-relaxed text-graphite-soft">
              {t("soutenir.noAmountNote")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest py-20 text-white md:py-28">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-10 px-6">
          <div className="max-w-2xl">
            <Reveal y={16}>
              <Overline light>{t("soutenir.overline")}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-4xl font-light tracking-tight" data-testid="paypal-block-title">
                {t("paypal.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-sm md:text-base leading-relaxed text-white/70">{t("paypal.text")}</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-white/50">{t("paypal.note")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <a
              href={PAYPAL_URL}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-3 bg-white px-8 py-5 font-mono text-xs uppercase tracking-[0.2em] text-graphite transition-colors duration-300 hover:bg-graphite hover:text-white"
              data-testid="paypal-donate-button"
            >
              {t("paypal.cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Reveal y={16}>
              <Overline>{t("soutenir.overline")}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-5xl font-light tracking-tight text-graphite">
                {t("soutenir.formTitle")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-graphite-soft">{t("soutenir.formText")}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-4">
                <CTAButton to="/partenariats" variant="outline" testid="soutenir-cta-partner">
                  {t("soutenir.otherCtas")[0]}
                </CTAButton>
                <CTAButton to="/programme" variant="outline" testid="soutenir-cta-project">
                  {t("soutenir.otherCtas")[1]}
                </CTAButton>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-3">
            <SubmissionForm formType="support" fields={fields} submitLabel={t("soutenir.formSubmit")} testid="support" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default SoutenirPage;
